import { useState } from "react";
import { AlertBanner } from "@/components/admin/alert-banner";
import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { financeApi } from "@/lib/finance/finance-api";
import { useRunExport } from "@/lib/finance/finance-hooks";
import type { ExportJob } from "@/lib/finance/types";
import { apiError, Field, inputStyle } from "./_ui";

const FORMATS = [
	{ value: "CSV", label: "CSV (UTF-8 BOM)" },
	{ value: "EXCEL_OHADA", label: "Excel OHADA (.xls)" },
	{ value: "SAGE_SAARI", label: "Sage Saari (.PNM — V2, fallback CSV)" },
	{ value: "TOMPRO", label: "Tompro (V2, fallback CSV)" },
];

/** FIN-08 — Exports comptables (Sage, Tompro, Excel OHADA, CSV). */
export function AdminExportsPage() {
	const run = useRunExport();
	const [form, setForm] = useState({
		format: "CSV",
		periodFrom: "2026-01-01",
		periodTo: "2026-12-31",
	});
	const [job, setJob] = useState<ExportJob | null>(null);
	const [err, setErr] = useState<string | null>(null);

	const launch = () => {
		setErr(null);
		setJob(null);
		run.mutate(form, {
			onSuccess: (j) => setJob(j),
			onError: (e) => setErr(apiError(e)),
		});
	};

	return (
		<>
			<PageHead
				title="Exports comptables"
				sub="Vers progiciels Cameroun · cabinet d'expertise-comptable"
			/>
			{err && <AlertBanner variant="danger" title={err} />}

			<Card>
				<CardHead title="Nouvel export" />
				<CardBody>
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
							gap: 12,
						}}
					>
						<Field label="Format">
							<select
								style={inputStyle}
								value={form.format}
								onChange={(e) => setForm({ ...form, format: e.target.value })}
							>
								{FORMATS.map((f) => (
									<option key={f.value} value={f.value}>
										{f.label}
									</option>
								))}
							</select>
						</Field>
						<Field label="Du">
							<input
								type="date"
								style={inputStyle}
								value={form.periodFrom}
								onChange={(e) =>
									setForm({ ...form, periodFrom: e.target.value })
								}
							/>
						</Field>
						<Field label="Au">
							<input
								type="date"
								style={inputStyle}
								value={form.periodTo}
								onChange={(e) => setForm({ ...form, periodTo: e.target.value })}
							/>
						</Field>
					</div>
					<div
						style={{
							display: "flex",
							justifyContent: "flex-end",
							marginTop: 12,
						}}
					>
						<AdminButton
							variant="primary"
							onClick={launch}
							disabled={run.isPending}
						>
							<AdminIcon n="download" s={14} />{" "}
							{run.isPending ? "Génération…" : "Lancer l'export"}
						</AdminButton>
					</div>
				</CardBody>
			</Card>

			{job && (
				<Card style={{ marginTop: 16 }}>
					<CardHead
						title={`Job ${job.jobId}`}
						right={
							<Tag
								variant={
									job.status === "DONE"
										? "success"
										: job.status === "FAILED"
											? "danger"
											: "info"
								}
							>
								{job.status}
							</Tag>
						}
					/>
					<CardBody>
						<div
							style={{
								display: "flex",
								gap: 24,
								flexWrap: "wrap",
								alignItems: "center",
							}}
						>
							<div>
								<div className="kpi-l muted">Lignes exportées</div>
								<div className="kpi-v tn">{job.rows}</div>
							</div>
							<div>
								<div className="kpi-l muted">Progression</div>
								<div className="kpi-v tn">{job.progress}%</div>
							</div>
							{job.status === "DONE" && (
								<AdminButton
									variant="primary"
									style={{ marginLeft: "auto" }}
									onClick={() =>
										financeApi.downloadExport(
											job.jobId,
											job.filename ?? "export",
										)
									}
								>
									<AdminIcon n="download" s={14} /> Télécharger {job.filename}
								</AdminButton>
							)}
						</div>
						{job.warnings.length > 0 && (
							<div style={{ marginTop: 12 }}>
								{job.warnings.map((w, i) => (
									<div key={i} className="muted" style={{ fontSize: 12 }}>
										⚠ {w}
									</div>
								))}
							</div>
						)}
					</CardBody>
				</Card>
			)}
		</>
	);
}
